import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Edit, MapPin, CheckCircle, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "./adminApi";

export default function AgenciesTab() {
  const [agencies, setAgencies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editAgency, setEditAgency] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", city: "", postal_code: "", email: "", phone: "", latitude: "", longitude: "", anael_code: "", anael_default_qualification: "", anael_default_category: "", anael_default_payment_mode: "V", anael_default_bulletin_form: "P", anael_default_bulletin_freq: "M", anael_default_issuer_code: "" });
  const fetchAgencies = useCallback(async () => { try { const r = await api.get("/agencies"); setAgencies(r.data); } catch {} }, []);
  useEffect(() => { fetchAgencies(); }, [fetchAgencies]);
  const resetForm = () => setForm({ name: "", address: "", city: "", postal_code: "", email: "", phone: "", latitude: "", longitude: "", anael_code: "", anael_default_qualification: "", anael_default_category: "", anael_default_payment_mode: "V", anael_default_bulletin_form: "P", anael_default_bulletin_freq: "M", anael_default_issuer_code: "" });

  const [geocoding, setGeocoding] = useState(false);

  const handleGeocode = async () => {
    if (!form.address || !form.city) { toast.error("Saisissez une adresse et une ville d'abord"); return; }
    setGeocoding(true);
    try {
      const query = `${form.address}, ${form.postal_code} ${form.city}`;
      const r = await api.get(`/geocode`, { params: { address: query } });
      if (r.data.latitude && r.data.longitude) {
        setForm((prev) => ({ ...prev, latitude: r.data.latitude, longitude: r.data.longitude }));
        toast.success(`Coordonnées trouvées : ${r.data.latitude.toFixed(4)}, ${r.data.longitude.toFixed(4)}`);
      } else {
        toast.error("Adresse non trouvée. Vérifiez l'adresse ou saisissez les coordonnées manuellement.");
      }
    } catch { toast.error("Erreur de géocodage"); }
    finally { setGeocoding(false); }
  };

  const handleSave = async () => {
    if (!form.name || !form.address || !form.city) { toast.error("Nom, adresse et ville requis"); return; }
    const payload = { ...form, latitude: form.latitude ? parseFloat(form.latitude) : null, longitude: form.longitude ? parseFloat(form.longitude) : null };
    try {
      if (editAgency) { await api.put(`/agencies/${editAgency.agency_id}`, payload); toast.success("Agence modifiée"); }
      else { await api.post("/agencies", payload); toast.success("Agence créée"); }
      setShowForm(false); setEditAgency(null); resetForm(); fetchAgencies();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const handleDelete = async (id) => { if (!window.confirm("Supprimer cette agence ?")) return; try { await api.delete(`/agencies/${id}`); toast.success("Agence supprimée"); fetchAgencies(); } catch { toast.error("Erreur"); } };
  const openEdit = (a) => { setEditAgency(a); setForm({ name: a.name, address: a.address, city: a.city, postal_code: a.postal_code || "", email: a.email || "", phone: a.phone || "", latitude: a.latitude || "", longitude: a.longitude || "", anael_code: a.anael_code || "", anael_default_qualification: a.anael_default_qualification || "", anael_default_category: a.anael_default_category || "", anael_default_payment_mode: a.anael_default_payment_mode || "V", anael_default_bulletin_form: a.anael_default_bulletin_form || "P", anael_default_bulletin_freq: a.anael_default_bulletin_freq || "M", anael_default_issuer_code: a.anael_default_issuer_code || "" }); setShowForm(true); };

  const [importing, setImporting] = useState(false);
  const handleImportCSV = async (file) => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/agencies/import", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
      const d = r.data;
      toast.success(`Import terminé : ${d.created} créée(s), ${d.skipped} ignorée(s), ${d.errors} erreur(s)`);
      if (d.skipped > 0 || d.errors > 0) {
        console.log("Détails import:", d.details);
      }
      fetchAgencies();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'import CSV");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-2">
        <p className="text-sm text-gray-500">{agencies.length} agence(s)</p>
        <div className="flex gap-2">
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) handleImportCSV(e.target.files[0]);
                e.target.value = "";
              }}
              data-testid="import-agencies-csv-input"
            />
            <Button
              variant="outline"
              className="rounded-none border-[#0069B4] text-[#0069B4] hover:bg-[#E6F0F7] text-xs h-9"
              disabled={importing}
              asChild
            >
              <span>
                <Upload className="w-4 h-4 mr-2" />
                {importing ? "Import en cours…" : "Importer CSV"}
              </span>
            </Button>
          </label>
          <Button onClick={() => { resetForm(); setEditAgency(null); setShowForm(true); }} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none font-semibold" data-testid="add-agency-btn"><Plus className="w-4 h-4 mr-2" /> Nouvelle agence</Button>
        </div>
      </div>
      <p className="text-[11px] text-gray-400 mb-3">Format CSV attendu : <code className="bg-gray-100 px-1">name,address,city,postal_code,email,phone</code> (séparateur , ou ;). Géocodage automatique — 1 agence/seconde.</p>
      <div className="space-y-3">
        {agencies.map((a) => (
          <div key={a.agency_id} className="bg-white border border-gray-200 p-4 flex items-center justify-between gap-4" data-testid={`admin-agency-${a.agency_id}`}>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-bold text-[#3F3F3F]">{a.name}</h3>
              <p className="text-xs text-gray-500">{a.address}, {a.postal_code} {a.city}</p>
              <p className="text-xs text-gray-400">{a.phone} {a.email ? `| ${a.email}` : ""}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => openEdit(a)} className="p-2 hover:bg-gray-100 text-[#0069B4]" data-testid={`edit-agency-${a.agency_id}`}><Edit className="w-4 h-4" /></button>
              <button onClick={() => handleDelete(a.agency_id)} className="p-2 hover:bg-gray-100 text-red-500" data-testid={`delete-agency-${a.agency_id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {agencies.length === 0 && <div className="text-center py-8 text-gray-500 text-sm">Aucune agence créée</div>}
      </div>
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg rounded-none">
          <DialogHeader><DialogTitle className="text-[#3F3F3F]">{editAgency ? "Modifier l'agence" : "Nouvelle agence"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label className="text-sm font-semibold mb-1 block">Nom de l'agence *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" placeholder="FR INTERIM Paris" data-testid="agency-form-name" /></div>
            <div><Label className="text-sm font-semibold mb-1 block">Adresse *</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-none" placeholder="12 rue de la Logistique" data-testid="agency-form-address" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm font-semibold mb-1 block">Code postal</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="rounded-none" placeholder="75001" data-testid="agency-form-postal" /></div>
              <div><Label className="text-sm font-semibold mb-1 block">Ville *</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-none" placeholder="Paris" data-testid="agency-form-city" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm font-semibold mb-1 block">Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none" placeholder="01 00 00 00 00" data-testid="agency-form-phone" /></div>
              <div><Label className="text-sm font-semibold mb-1 block">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" placeholder="paris@frinterim.com" data-testid="agency-form-email" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm font-semibold mb-1 block">Latitude</Label><Input type="number" step="any" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} className="rounded-none" placeholder="48.8566" data-testid="agency-form-lat" /></div>
              <div><Label className="text-sm font-semibold mb-1 block">Longitude</Label><Input type="number" step="any" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} className="rounded-none" placeholder="2.3522" data-testid="agency-form-lng" /></div>
            </div>
            <Button type="button" variant="outline" onClick={handleGeocode} disabled={geocoding} className="rounded-none border-[#662483] text-[#662483] text-sm w-full" data-testid="geocode-btn">
              <MapPin className="w-4 h-4 mr-2" /> {geocoding ? "Géolocalisation en cours..." : "Géolocaliser automatiquement à partir de l'adresse"}
            </Button>
            {form.latitude && form.longitude && (
              <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Coordonnées : {Number(form.latitude).toFixed(4)}, {Number(form.longitude).toFixed(4)}</p>
            )}
            <div>
              <Label className="text-sm font-semibold mb-1 block">Code agence Anael (PERAGE)</Label>
              <Input value={form.anael_code} onChange={(e) => setForm({ ...form, anael_code: e.target.value })} className="rounded-none" placeholder="ex. PAR ou 001" data-testid="agency-form-anael-code" />
              <p className="text-[10px] text-gray-400 mt-1">Code agence utilisé pour la synchronisation des salariés vers l'outil métier Anael TT. Laissez vide si non utilisé.</p>
            </div>

            <div className="border border-[#F0E9F3] bg-[#FAF5FE] p-3 space-y-3">
              <p className="text-xs font-bold text-[#662483] uppercase tracking-wider">Anael — Défauts paie (par agence)</p>
              <p className="text-[11px] text-gray-500 -mt-1">Appliqués automatiquement à tous les salariés rattachés à l'agence, sauf si le candidat dispose d'une valeur propre.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Qualification par défaut (PERQUA)</Label>
                  <Input value={form.anael_default_qualification} onChange={(e) => setForm({ ...form, anael_default_qualification: e.target.value })} className="rounded-none text-sm h-9" placeholder="ex. AGE202" data-testid="agency-anael-default-qua" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Catégorie par défaut (PERCAT)</Label>
                  <Input value={form.anael_default_category} onChange={(e) => setForm({ ...form, anael_default_category: e.target.value })} className="rounded-none text-sm h-9" placeholder="ex. NCA E" data-testid="agency-anael-default-cat" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Mode règlement (PERMOD)</Label>
                  <Input value={form.anael_default_payment_mode} onChange={(e) => setForm({ ...form, anael_default_payment_mode: e.target.value.toUpperCase() })} className="rounded-none text-sm h-9 uppercase" placeholder="V (Virement) / C (Chèque)" data-testid="agency-anael-default-mod" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Forme bulletin (PERFOR)</Label>
                  <Input value={form.anael_default_bulletin_form} onChange={(e) => setForm({ ...form, anael_default_bulletin_form: e.target.value.toUpperCase() })} className="rounded-none text-sm h-9 uppercase" placeholder="P (multi-contrat détail)" data-testid="agency-anael-default-form" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Fréquence bulletin (PERFRE)</Label>
                  <Input value={form.anael_default_bulletin_freq} onChange={(e) => setForm({ ...form, anael_default_bulletin_freq: e.target.value.toUpperCase() })} className="rounded-none text-sm h-9 uppercase" placeholder="M (mois civil)" data-testid="agency-anael-default-freq" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Organisme de délivrance (PERORG)</Label>
                  <Input value={form.anael_default_issuer_code} onChange={(e) => setForm({ ...form, anael_default_issuer_code: e.target.value.toUpperCase() })} className="rounded-none text-sm h-9 uppercase" placeholder="PP / PR / MA / SP / CO" data-testid="agency-anael-default-issuer" />
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-400">Vous pouvez aussi trouver les coordonnées GPS manuellement sur Google Maps (clic droit sur la carte).</p>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleSave} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none font-semibold" data-testid="agency-form-save">{editAgency ? "Modifier" : "Créer"}</Button>
              <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-none">Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
